/**
 * MealSelectionCard component
 * Individual meal card with selection controls
 */

import { useTranslation } from 'react-i18next';
import type { AvailableMeal } from '@/types/api';
import { capitalize } from '@/lib/utils/text';

interface MealSelectionCardProps {
  meal: AvailableMeal;
  selectedCount: number;
  onIncrement: () => void;
  onDecrement: () => void;
  disabled?: boolean;
}

/**
 * MealSelectionCard component
 *
 * Features:
 * - Display meal name and tags
 * - Increment/decrement selection count
 * - Touch-friendly 44px buttons
 * - Visual feedback for selected state
 * - Accessible controls
 *
 * @example
 * ```tsx
 * <MealSelectionCard
 *   meal={meal}
 *   selectedCount={2}
 *   onIncrement={() => handleSelect(meal.id)}
 *   onDecrement={() => handleDeselect(meal.id)}
 * />
 * ```
 */
export function MealSelectionCard({
  meal,
  selectedCount,
  onIncrement,
  onDecrement,
  disabled = false,
}: MealSelectionCardProps) {
  const { t } = useTranslation();

  const isSelected = selectedCount > 0;

  return (
    <div
      className={`
        bg-white rounded-lg shadow-md p-4 transition-all
        ${isSelected ? 'ring-2 ring-primary-500 shadow-lg' : 'hover:shadow-lg'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      {/* Meal name */}
      <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
        {capitalize(meal.name)}
      </h3>

      {/* Tags */}
      {meal.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {meal.tags
            .filter((tag) => !tag.isHidden)
            .map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800"
              >
                {capitalize(tag.tag)}
              </span>
            ))}
        </div>
      )}


      {/* Selection controls */}
      <div className="flex items-center justify-between">
        {/* Decrement button */}
        <button
          type="button"
          onClick={onDecrement}
          disabled={disabled || selectedCount === 0}
          className="min-h-touch min-w-touch p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          aria-label={t('weeklySelection.removeMeal')}
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>

        {/* Selection count */}
        <div className="flex items-center gap-2">
          {isSelected && (
            <span className="text-sm font-medium text-primary-600">{t('menu.selection')}</span>
          )}
          <span
            className={`text-2xl font-bold ${isSelected ? 'text-primary-600' : 'text-gray-400'}`}
            aria-live="polite"
            aria-atomic="true"
          >
            {selectedCount}
          </span>
        </div>

        {/* Increment button */}
        <button
          type="button"
          onClick={onIncrement}
          disabled={disabled}
          className="min-h-touch min-w-touch p-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          aria-label={t('weeklySelection.addMeal')}
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
        </button>
      </div>

      {/* Recommendation badge (if available) */}
      {meal.recommendationScore !== undefined && meal.recommendationScore > 70 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1 text-xs text-green-600 font-medium">
            <svg
              className="h-4 w-4"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>{t('menu.recommended')}</span>
          </div>
        </div>
      )}
    </div>
  );
}
